export interface ParsedSyslog {
  isSyslog: boolean;
  action?: string;
  params: Record<string, string>;
  text: string;
}

/**
 * Parses @@syslog_* formatted messages into human-readable event descriptions.
 * Example:
 *   "@@syslog_moved_to_stage|name=Younis Panwar|stage=Won"
 *   -> "Moved to stage "Won" by Younis Panwar"
 * 
 *   "@@syslog_moved_to_stage|name=Younis Panwar|stage=Won|orderId=ORD-9513328072"
 *   -> "Moved to stage "Won" by Younis Panwar (Order #ORD-9513328072)"
 */
export function parseSyslog(text?: string | null): ParsedSyslog {
  if (!text || typeof text !== 'string' || !text.startsWith('@@syslog_')) {
    return {
      isSyslog: false,
      params: {},
      text: text || '',
    };
  }

  const raw = text.replace(/^@@syslog_/, '');
  const parts = raw.split('|');
  const action = parts[0];
  const params: Record<string, string> = {};

  for (let i = 1; i < parts.length; i++) {
    const [k, ...v] = parts[i].split('=');
    if (k) params[k.trim()] = v.join('=').trim();
  }

  let friendlyText = text;
  if (action === 'moved_to_stage') {
    const stage = params.stage || 'Won';
    const byName = params.name ? ` by ${params.name}` : '';
    const order = params.orderId ? ` (Order #${params.orderId})` : '';
    friendlyText = `Moved to stage "${stage}"${byName}${order}`;
  } else if (action === 'ai_moved_to_stage') {
    friendlyText = `AI moved to stage "${params.stage || ''}"`;
  } else if (action === 'transferred_to') {
    friendlyText = `Transferred to ${params.agent || 'agent'}`;
  } else if (action === 'auto_assigned') {
    friendlyText = `Auto-assigned to ${params.agent || 'agent'}`;
  } else if (action === 'ai_auto_assigned_offline' || action === 'ai_auto_assigned') {
    friendlyText = `Auto-assigned to AI Agent (Team offline)`;
  } else if (action === 'ai_no_agent_reply') {
    const secStr = params.seconds ? `${params.seconds}s` : 'timeout';
    friendlyText = `AI Agent took over (No agent reply within ${secStr})`;
  } else if (action === 'unassigned') {
    friendlyText = `Unassigned by ${params.name || 'team'}`;
  } else if (action === 'chat_closed') {
    friendlyText = `Chat closed by ${params.name || 'team'}`;
  } else if (action === 'department_assigned') {
    friendlyText = `Department "${params.department || ''}" assigned by ${params.name || 'team'}`;
  } else {
    friendlyText = action.replace(/_/g, ' ');
    if (params.name) friendlyText += ` by ${params.name}`;
    if (params.stage) friendlyText += ` to ${params.stage}`;
    if (params.orderId) friendlyText += ` (Order #${params.orderId})`;
  }

  return {
    isSyslog: true,
    action,
    params,
    text: friendlyText,
  };
}

/**
 * Returns clean preview text for chat lists, cards, and notification feeds.
 */
export function formatSyslogPreview(text?: string | null): string {
  if (!text) return '';
  const parsed = parseSyslog(text);
  return parsed.isSyslog ? parsed.text : text;
}
